import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  AccordionProps,
  AccordionSummaryProps,
  AccordionDetailsProps,
  SxProps,
  Theme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export interface CustomAccordionProps {
  title: string;
  count?: number;
  expanded: boolean;
  onChange: () => void;
  children: React.ReactNode;
  /** Optional actions rendered on the right side of the summary (e.g. refresh button) */
  summaryActions?: React.ReactNode;
  sx?: SxProps<Theme>;
  summarySx?: SxProps<Theme>;
  detailsSx?: SxProps<Theme>;
  accordionProps?: Partial<AccordionProps>;
  summaryProps?: Partial<AccordionSummaryProps>;
  detailsProps?: Partial<AccordionDetailsProps>;
}

const CustomAccordion: React.FC<CustomAccordionProps> = ({
  title,
  count,
  expanded,
  onChange,
  children,
  summaryActions,
  sx,
  summarySx,
  detailsSx,
  accordionProps,
  summaryProps,
  detailsProps,
}) => {
  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={{
        mt: 1.25,
        boxShadow: 'none',
        border: (theme: Theme) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          margin: '0 !important',
        },
        '& .MuiAccordionSummary-root': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
        },
        '& .MuiAccordionDetails-root': {
          py: 1,
          px: 2,
        },
        ...sx,
      }}
      {...accordionProps}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: (theme: Theme) => theme.palette.neutral1?.main || '#F6F7FB',
          fontWeight: 600,
          py: 0,
          my: 0,
          px: 1.5,
          '& .MuiAccordionSummary-content': {
            my: 0,
            flexGrow: 1,
            minWidth: 0,
          },
          ...summarySx,
        }}
        {...summaryProps}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            mr: 0.5,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '14px' }}>
            {title} {count !== undefined && count > 0 && `(${count})`}
          </Typography>
          {summaryActions}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          p: 1.5,
          ...detailsSx,
        }}
        {...detailsProps}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default CustomAccordion;
